import { useFilters } from "@/context/FiltersContext";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mShort = (y, m) => new Date(y, m, 1).toLocaleDateString("en-US", { month: "short" });
const yy = (y) => String(y).slice(2);

// The list of months covered by the current shared date range (capped at 14).
export function monthsInRange(dateRange) {
  const base = dateRange.from ? new Date(dateRange.from + "T00:00:00") : new Date();
  const end = dateRange.to ? new Date(dateRange.to + "T00:00:00") : base;
  const months = [];
  let y = base.getFullYear(), m = base.getMonth();
  const ey = end.getFullYear(), em = end.getMonth();
  while (y < ey || (y === ey && m <= em)) { months.push({ y, m }); m++; if (m > 11) { m = 0; y++; } if (months.length >= 14) break; }
  if (!months.length) months.push({ y: base.getFullYear(), m: base.getMonth() });
  return months;
}

// Shared month navigator (‹ Month › + Today). Driven by the shared dateRange so Calendar
// and Map always stay in sync — navigating a month on one view moves the other too.
export function MonthNav({ dark = false, hideToday = false }) {
  const { dateRange, setCustomRange } = useFilters();
  const months = monthsInRange(dateRange);
  const goto = (delta) => { const d = new Date(months[0].y, months[0].m + delta, 1); setCustomRange(iso(d), iso(new Date(d.getFullYear(), d.getMonth() + 1, 0))); };
  const today = () => { const t = new Date(); setCustomRange(iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(new Date(t.getFullYear(), t.getMonth() + 1, 0))); };
  const a = months[0], b = months[months.length - 1];
  const text = months.length > 1
    ? (a.y === b.y ? `${mShort(a.y, a.m)} – ${mShort(b.y, b.m)} ${yy(b.y)}` : `${mShort(a.y, a.m)} ${yy(a.y)} – ${mShort(b.y, b.m)} ${yy(b.y)}`)
    : `${mShort(a.y, a.m)} ${yy(a.y)}`;
  const border = dark ? "border-white/15" : "border-[#E4E7EB]";
  const hover = dark ? "hover:bg-white/10" : "hover:bg-[#F1F3F5]";
  const txt = dark ? "text-slate-200" : "text-slate-800";
  return (
    <div className="flex items-center gap-1" data-testid="month-nav">
      <div className={`flex items-center shrink-0 border ${border} rounded-lg ${txt}`}>
        <button onClick={() => goto(-1)} data-testid="cal-prev-month" className={`px-2 py-1.5 ${hover}`}><ChevronLeft className="w-4 h-4" /></button>
        <span className="px-1.5 py-1.5 text-[13px] font-semibold text-center whitespace-nowrap" data-testid="cal-month-label">{text}</span>
        <button onClick={() => goto(1)} data-testid="cal-next-month" className={`px-2 py-1.5 ${hover}`}><ChevronRight className="w-4 h-4" /></button>
      </div>
      <button onClick={today} data-testid="cal-this-month" className={`${hideToday ? "hidden" : "flex"} items-center gap-1.5 border ${border} rounded-lg px-2 py-1.5 text-sm font-medium ${hover} ${txt}`}>
        <CalendarDays className="w-4 h-4" /> <span>Today</span>
      </button>
    </div>
  );
}
